// ======================================================================
// File Name       : CourseDetail
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import moment from 'moment';
import PropTypes from 'prop-types';
import { Col, Form, Row, Table } from 'react-bootstrap';
import * as CourseContants from '../../constants/CourseConstants';
import { CERTIFICATE_USE_FLAG } from '../../constants/CourseConstants';
import { DEFAULT_DATE_FORMAT_SHORT_DETAIL } from '../../constants/index';
import * as MessageTemplate from '../../constants/MessageTemplate';
import {
  displayUserAuthority,
  MAX_DATE_NO_SECOND,
  MIN_DATE_NO_SECOND,
  MIN_MAX_DATE_TXT,
  parseDateForDetail
} from '../../utils';
import * as courseUtils from '../../utils/CourseUtils';
import { Category, Item } from '../Detail';
import TableHead from '../TableHead';

const CourseRelation = ({
  data,
  users,
  courseGroupRelation,
  groupList,
  authority,
  isShowMessageMaxRecord,
  isShowMessageGroupMaxRecord,
  isDetail,
}) => {
  const convertAttendingPeriodDate = (attendingPeriodDate) => {
    let attendingPeriodDateTxt = '';
    try {
      // #7230 START
      if (
        attendingPeriodDate &&
        (moment(attendingPeriodDate).isSameOrBefore(MIN_DATE_NO_SECOND) ||
          moment(attendingPeriodDate).isSameOrAfter(MAX_DATE_NO_SECOND))
      ) {
        attendingPeriodDateTxt = MIN_MAX_DATE_TXT;
      } else {
        attendingPeriodDateTxt = parseDateForDetail(attendingPeriodDate, DEFAULT_DATE_FORMAT_SHORT_DETAIL);
      }
      // #7230 END
    } catch (error) {
      attendingPeriodDateTxt = attendingPeriodDate;
    }
    return attendingPeriodDateTxt;
  };
  const tbody = Object.keys(data).map((userSecernId, index) => {
    const { attendingPeriodFrom, attendingPeriodTo } = data[userSecernId];
    const attendingPeriodFromTxt = convertAttendingPeriodDate(attendingPeriodFrom);
    const attendingPeriodToTxt = convertAttendingPeriodDate(attendingPeriodTo);

    const user = users[userSecernId] || data[userSecernId];
    if (!user) {
      return null;
    }
    const { userType, managementAuthoritySecernId, userName, loginId } = user;

    return (
      <tr key={index}>
        <td>{displayUserAuthority(user, authority)}</td>
        <td>{userName}</td>
        <td>{loginId}</td>
        <td>{attendingPeriodFromTxt}</td>
        <td>{attendingPeriodToTxt}</td>
      </tr>
    );
  });

  const groupListObj = groupList.reduce((obj, cur) => {
    obj[cur.groupSecernId] = cur;
    return obj;
  }, {});

  return (
    <div>
      <Row className='margin-bottom-15'>
          <Col sm={12} className="msg-error bold padding-left-right-clear">
            {isDetail && isShowMessageMaxRecord
              ? MessageTemplate.MSG_RESULT_OVER_MAX_RETURN_NUM
              : ""}
          </Col>
          <Col sm={3} className="padding-left-right-clear margin-top-10">
            紐付け対象ユーザー
          </Col>
          <Col sm={12} className="padding-left-right-clear">
            <Table striped hover className="margin-top-10">
              <thead>
                <tr>
                  <th>ロール/ユーザー種別</th>
                  <th>ユーザー名</th>
                  <th>ログインID</th>
                  <th>有効期限開始日時</th>
                  <th>有効期限終了日時</th>
                </tr>
              </thead>
              <tbody>{tbody}</tbody>
            </Table>
          </Col>
      </Row>
      <Row className='margin-bottom-15'>
        <Col sm={12} className="msg-error bold padding-left-right-clear">
          {isDetail && isShowMessageGroupMaxRecord
            ? MessageTemplate.MSG_RESULT_OVER_MAX_RETURN_NUM
            : ""}
        </Col>
        <Col sm={12} className="padding-left-right-clear margin-top-10">
          <Col sm={3} className="padding-left-right-clear">
            紐付け対象ユーザーグループ
          </Col>
        </Col>
        <Col
          sm={12}
          className="padding-left-right-clear margin-top-10 textColorRed"
        >
          ※新しいレイアウトで設定したユーザーグループ紐づけは、古いレイアウトでは表示されません
        </Col>
        <Col sm={12} className="padding-left-right-clear">
          <Table striped hover className="margin-top-10">
            <thead>
              <tr>
                <th>ユーザーグループ名</th>
                <th>有効期限開始日時</th>
                <th>有効期限終了日時</th>
              </tr>
            </thead>
            <tbody>
              {courseGroupRelation.map((group, index) => {
                const { attendingPeriodFrom, attendingPeriodTo } = group;
                const attendingPeriodFromTxt =
                  convertAttendingPeriodDate(attendingPeriodFrom);
                const attendingPeriodToTxt =
                  convertAttendingPeriodDate(attendingPeriodTo);
                let groupName = "";
                if (
                  Object.keys(groupListObj).length &&
                  groupListObj[group.groupSecernId]
                ) {
                  groupName = groupListObj[group.groupSecernId].groupName;
                }
                return (
                  <tr key={index}>
                    <td>{groupName}</td>
                    <td>{attendingPeriodFromTxt}</td>
                    <td>{attendingPeriodToTxt}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  );
};

CourseRelation.propTypes = {
  data: PropTypes.shape().isRequired,
  users: PropTypes.shape().isRequired,
  authority: PropTypes.shape().isRequired,
};

CourseRelation.defaultProps = {
  data: {},
  users: {},
  authority: {},
};

const coursePropTypes = {
  course: PropTypes.shape().isRequired,
};

const BasicSetting = ({
  course: {
    courseName,
    description,
    publicStatus,
    dataUrl,
    imageUploadFile,
    thumbnailUrl,
    imageFileSelectType,
    defaultThumbnail,
  },
  editType,
}) => {
  const publicStatusDisplayValue = publicStatus === 1 ? '公開' : '非公開';
  let urlImg = <img src={defaultThumbnail} className="img-thumbnail" width="150" height="150" />;
  if (thumbnailUrl) {
    urlImg = <img src={thumbnailUrl} className="img-thumbnail" width="150" height="150" />;
  }
  if (imageUploadFile) {
    urlImg = <img src={dataUrl} className="img-thumbnail" width="150" height="150" />;
  }
  return (
    <div>
      <Category label="基本設定" />
      <Item name="コース名" value={courseName} indent={1} />
      <Item name="説明" value={description} indent={1} />
      <Item name="公開ステータス" value={publicStatusDisplayValue} indent={1} />
      <Item name="サムネイル画像" value={urlImg} indent={1} formClassName="label-middle" />
    </div>
  );
};
BasicSetting.propTypes = coursePropTypes;

function DetailSetting({
  course: { endConditionTypeForCourse, publicPeriodFrom, publicPeriodTo, completionFlag, progressRatioForCourseEnd },
  structures,
  selectedItemList,
}) {
  const endConditionDisplayValue = endConditionTypeForCourse === 0 ? 'なし' : 'あり';
  const completionFlagValue = completionFlag === CERTIFICATE_USE_FLAG.NO_USE ? '利用なし' : '利用あり';

  const displayPublicPerido = courseUtils.getDisplayPublicPeriod(publicPeriodFrom, publicPeriodTo);
  // Step 10.2.0 START
  const displayEndConditionTypeForCourse = courseUtils.getDisplayEndConditionTypeForCourse(
    endConditionTypeForCourse,
    progressRatioForCourseEnd || 0,
  );
  const thead = [
    { title: "コース表示名称", sortBy: null },
    { title: "親セクション名", sortBy: null },
  ];

  let dataTable = null;
  if (selectedItemList && selectedItemList.length) {
    dataTable = selectedItemList.map((cur, index) => {
      let itemNameWithTable = '';
      let boxNameWithItem = '';
      let boxIdWithItem = '';
      if (
        Object.keys(structures.bySecernId).length &&
        structures.bySecernId[cur.itemSecernId]
      ) {
        itemNameWithTable = structures.bySecernId[cur.itemSecernId].structureName;
        boxIdWithItem = structures.bySecernId[cur.itemSecernId].parentBoxSecernId;
        boxNameWithItem = structures.bySecernId[boxIdWithItem].structureName;
      }
      return (
        <tr key={index}>
          <td>{itemNameWithTable}</td>
          <td>{boxNameWithItem}</td>
        </tr>
      );
    });
  }
  const endConditionDataTable = (
    <Table striped hover>
      <thead>
        <TableHead data={thead} />
      </thead>
      <tbody>{dataTable}</tbody>
    </Table>
  );

  const isEnableCourseEndCondition = courseUtils.isEnableCourseEndCondition(endConditionTypeForCourse);
  // Step 10.2.0 END

  return (
    <div>
      <Category label="詳細設定" />
      {/* Step 10.2.0 START */}
      <Form.Group className="form-info">
        <Col sm={12} className="margin-top-10">
          <Col sm={3} as={Form.Label}>
            <div className='padding-left-15'>
              コース完了条件
            </div>
          </Col>
          {isEnableCourseEndCondition && (
            <Col sm={9} as={Form.Label}>
              {displayEndConditionTypeForCourse}
            </Col>
          )}
          {!isEnableCourseEndCondition && (
            <Col sm={9} as={Form.Label} className="textColorRed">※ 新条件が適用されています。新レイアウトで設定を確認ください。</Col>
          )}
        </Col>
        {isEnableCourseEndCondition && endConditionTypeForCourse !== CourseContants.END_CONDITION_TYPE_FOR_COURSE.NONE && (
          <Col sm={12} className="margin-top-10 padding-left-30 padding-right-15">
            {endConditionDataTable}
          </Col>
        )}
      </Form.Group>
      {/* Step 10.2.0 END */}
      <Item name="公開期間" value={displayPublicPerido} indent={1} />
      <Item name="修了証の利用" value={completionFlagValue} indent={1} />
    </div>
  );
};
DetailSetting.propTypes = coursePropTypes;

export { BasicSetting, CourseRelation, DetailSetting };
